import { DEV } from 'cc/env';
import { Button, EventTouch, Node, _decorator } from 'cc';
import Delay from '../../Utils/Delay';
import Gesture from '../../Utils/Gesture';
import NumberFormatter from '../Label/NumberFormatter';
import Processor from '../Misc/Processor';

const { ccclass, property } = _decorator;

enum EventType {
    VALUE_CHANGED = 'OnValueChange',
}

@ccclass
export default abstract class ValuePicker extends Processor {
    public static readonly EVENT_TYPE = { ...EventType, ...Processor.EVENT_TYPE };

    @property(Button)
    public readonly increaseButton: Button = null;

    @property(Button)
    public readonly decreaseButton: Button = null;

    @property(NumberFormatter)
    protected readonly valueNumber: NumberFormatter = null;

    @property({ tooltip: DEV && '是否啟用長按持續變化數值' })
    protected readonly enableRunning: boolean = true;

    @property({
        tooltip: DEV && '長按開始後延遲多長時間開始變化數值 (in milliseconds)',
        visible(this: ValuePicker) {
            return this.enableRunning;
        },
        formerlySerializedAs: 'startRunningDelay',
    })
    private _startRunningDelay: number = 0;

    @property({
        tooltip: DEV && '啟用此項時，如果離開按鈕範圍會暫停數值變化',
        visible(this: ValuePicker) {
            return this.enableRunning;
        },
    })
    private readonly suspendable: boolean = false;

    @property({
        tooltip: DEV && '啟用此項時，如果離開按鈕範圍會直接結束長壓判斷',
        visible(this: ValuePicker) {
            return this.suspendable && this.enableRunning;
        },
        formerlySerializedAs: 'interruptWhenOutsideButton',
    })
    private readonly abortOnceSuspend: boolean = true;

    @property
    private _interactable = true;

    protected startRunningTime: number = null;
    protected pauseDuration = 0;
    private _startPausingTime: number = null;
    private _isPreviousTouchIsInside: boolean = null;
    private _value: number = 0;
    private _isHoldingButton: boolean = false;
    private _hasRun: boolean = false;
    private _leaveButtonPromise: Promise<void> = null;
    private _leaveButtonPromiseResolve: () => void = null;

    public get value() {
        return this._value;
    }
    public set value(value: number) {
        if (this.valueNumber) {
            this.valueNumber.value = value;
        }
        this._value = value;
        this.node.emit(EventType.VALUE_CHANGED, value);
    }

    @property
    public get interactable() {
        return this._interactable;
    }
    public set interactable(flag: boolean) {
        if (this._interactable === flag) {
            return;
        }
        this._interactable = flag;
        this.updateInteractable();
    }

    /**
     * 已開始跑動多長時間 (in milliseconds)
     */
    protected get pastRunningTime() {
        if (this.startRunningTime === null) {
            return 0;
        }
        return Math.max(Date.now() - this.startRunningTime - this.pauseDuration, 1);
    }

    public start() {
        this.updateInteractable();
    }

    public onLoad() {
        this._startRunningDelay = this._startRunningDelay === -1 ? Number.POSITIVE_INFINITY : this._startRunningDelay;
        if (this.increaseButton) {
            this._configureButtonSetting(this.increaseButton, true);
        }
        if (this.decreaseButton) {
            this._configureButtonSetting(this.decreaseButton, false);
        }
    }

    public async play(isUpward: boolean) {
        super.play(isUpward);
    }

    protected async finish() {
        this.startRunningTime = null;
        this.pauseDuration = 0;
    }

    protected updateInteractable() {
        if (this.increaseButton) {
            this.increaseButton.interactable = this.interactable;
        }
        if (this.decreaseButton) {
            this.decreaseButton.interactable = this.interactable;
        }
    }

    private _configureButtonSetting(button: Button, isUpward: boolean) {
        let isButtonHold = false;
        button.node.on(Node.EventType.TOUCH_START, async () => {
            if (!button.interactable || !this.enableRunning) {
                return;
            }
            if (this._isHoldingButton) {
                return;
            }
            this._hasRun = false;
            this._isHoldingButton = true;
            isButtonHold = true;
            this._isPreviousTouchIsInside = true;
            this._leaveButtonPromise = new Promise<void>((resolve) => {
                this._leaveButtonPromiseResolve = resolve;
            });
            const result = await Promise.race([Delay.value(this._startRunningDelay, true), this._leaveButtonPromise.then(() => false)]);
            if (isButtonHold && result) {
                this._hasRun = true;
                this.play(isUpward);
            }
        });
        button.node.on(Node.EventType.TOUCH_END, () => {
            if (!button.interactable) {
                return;
            }
            if (this.enableRunning) {
                const isSelfHoldingButton = isButtonHold && this._isHoldingButton;
                if (isSelfHoldingButton) {
                    this._isHoldingButton = false;
                }
                isButtonHold = false;
                if (!isSelfHoldingButton) {
                    return;
                }
                if (this._leaveButtonPromiseResolve) {
                    this._leaveButtonPromiseResolve();
                    this._leaveButtonPromiseResolve = null;
                }
                this._isPreviousTouchIsInside = null;
                if (this._hasRun) {
                    this.stop();
                    return;
                }
            }
            this.stepOnce(isUpward, true);
        });
        button.node.on(Node.EventType.TOUCH_CANCEL, () => {
            if (!button.interactable || !this.enableRunning) {
                return;
            }
            const isSelfHoldingButton = isButtonHold && this._isHoldingButton;
            if (isSelfHoldingButton) {
                this._isHoldingButton = false;
            }
            isButtonHold = false;
            if (!isSelfHoldingButton) {
                return;
            }
            if (this._leaveButtonPromiseResolve) {
                this._leaveButtonPromiseResolve();
                this._leaveButtonPromiseResolve = null;
            }
            this._isPreviousTouchIsInside = null;
            this.stop();
        });
        button.node.on(Button.EventType.INTERACTABLE_CHANGED, () => {
            if (!button.interactable && isButtonHold) {
                const isSelfHoldingButton = isButtonHold && this._isHoldingButton;
                if (isSelfHoldingButton) {
                    this._isHoldingButton = false;
                }
                isButtonHold = false;
                if (!isSelfHoldingButton) {
                    return;
                }
                if (this._leaveButtonPromiseResolve) {
                    this._leaveButtonPromiseResolve();
                    this._leaveButtonPromiseResolve = null;
                }
                if (this.isRunning) {
                    this.stop();
                }
            }
        });
        if (this.suspendable && this.enableRunning) {
            button.node.on(Node.EventType.TOUCH_MOVE, (event: EventTouch) => {
                if (!button.interactable) {
                    return;
                }
                if (this._isPreviousTouchIsInside === null) {
                    this._isPreviousTouchIsInside = Gesture.Click.insideTarget(event);
                    return;
                }
                const currentTouchIsInside = Gesture.Click.insideTarget(event);
                if (this._isPreviousTouchIsInside === currentTouchIsInside) {
                    return;
                }
                if (currentTouchIsInside) {
                    // 回到按鈕內
                    this.resume();
                    this.pauseDuration += Date.now() - this._startPausingTime;
                }
                // 離開按鈕時 判斷要直接中斷還是暫停
                else if (this.abortOnceSuspend) {
                    this.stop(true);
                    if (isButtonHold && this._leaveButtonPromiseResolve) {
                        this._leaveButtonPromiseResolve();
                        this._leaveButtonPromiseResolve = null;
                    }
                } else {
                    this.pause();
                    this._startPausingTime = Date.now();
                }
                this._isPreviousTouchIsInside = currentTouchIsInside;
            });
        }
    }

    protected abstract stepOnce(isUpward: boolean, isClick: boolean): void;
}
